import { CheckCircleTwoTone, QuestionCircleTwoTone, ThunderboltTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { spiritualAwarenessChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'Why should I take time to reflect on who I am?',
    answer: (
      <>
        Reflection helps you notice patterns in your choices and feelings that you'd otherwise miss while rushing through life. This same
        kind of self-check-in is used in <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link> to understand your
        own reactions.
      </>
    ),
  },
  {
    question: 'How do I figure out my values and what matters to me?',
    answer: (
      <>
        Ask yourself: what makes me feel alive? What do I stand up for? Who do I want to be? Once you know your values, checking whether
        your actions match them is the same kind of accountability discussed in{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link>.
      </>
    ),
  },
  {
    question: 'How do I find my purpose or what gives my life meaning?',
    answer: (
      <>
        Purpose often comes from what you're good at, what you care about, and what the people around you need. It doesn't have to be one
        big thing — it can be as simple as being someone your <Link to="/adulting-101/friends-and-family">Friends and Family</Link> can
        count on.
      </>
    ),
  },
  {
    question: "How do I practice mindfulness if I'm always busy?",
    answer: (
      <>
        Mindfulness doesn't require hours — just being present for a few minutes helps. Pay attention during everyday activities instead of
        rushing through them, the same kind of focus described in <Link to="/adulting-101/time-management">Time Management</Link> when you
        limit distractions.
      </>
    ),
  },
  {
    question: 'Why does practicing gratitude matter, especially on hard days?',
    answer: (
      <>
        Noticing something good, even on a bad day, trains your brain to see more than just the negative. It's the same mindset shift that
        helps you stay positive while working through a tough situation in <Link to="/adulting-101/problem-solving">Problem Solving</Link>.
      </>
    ),
  },
  {
    question: 'Why is forgiving myself or others so important?',
    answer: (
      <>
        Holding onto guilt or resentment keeps you stuck in the past. Forgiveness doesn't mean excusing bad behavior — it means releasing
        the weight so you can move forward, similar to how a sincere apology helps repair a relationship in{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: "What if I don't believe in God or religion? How do I feel 'connected' to something bigger?",
    answer: (
      <>
        That's okay — connection can come from nature, community, art, or the people you love. It's about knowing your place in the world,
        the same sense of belonging found in <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: 'How does learning about myself actually help me grow?',
    answer: (
      <>
        Every insight about yourself — what triggers you, what you're proud of — gives you information to make better choices next time.
        This is the same continuous improvement described in <Link to="/adulting-101/problem-solving">Problem Solving</Link>, where you
        learn from what didn't work and adjust.
      </>
    ),
  },
]

export function Adulting101SpiritualAwarenessPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Spiritual Awareness</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #f6f0ff 0%, #ffffff 220px)' }}>
        <Title level={2}>Spiritual awareness is knowing who you are, what you value, and what gives your life meaning.</Title>
        <Paragraph type="secondary">
          This doesn't require religion. It's about understanding yourself deeply, living according to your values, and feeling connected to
          something greater than yourself. It brings peace, direction, and purpose to your life.
        </Paragraph>

        <Card
          title={
            <Space>
              <ThunderboltTwoTone twoToneColor="#faad14" />
              Spiritual Awareness Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you\'re developing spiritual awareness!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Spiritual awareness is a lifelong journey. The more you understand yourself and your values, the more peace and purpose
              you\'ll find.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Spiritual Awareness
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
